import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Divider from '@zenbusiness/zui/Divider';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import useBreakpoints from '#client/hooks/useBreakpoints';

const CardContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  borderRadius: 12,
  border: `1px solid ${theme.palette.border.main}`,
  backgroundColor: theme.palette.background.grey.light,
  color: theme.palette.text.primary,
  boxShadow: '0px 4px 16px -4px rgba(15, 22, 28, 0.06), 0px 8px 32px -8px rgba(15, 22, 28, 0.12)'
}));

const CardContent = styled(Stack)(({ theme }) => ({
  paddingInline: theme.spacing(1)
}));

const CardAction = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5)
}));

const CardLogo = ({ logo }) => {
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  return (
    <Stack
      alignItems="center"
      height={isDesktop ? 48 : 'auto'}
      justifyContent="center"
    >
      <SharedAsset
        alt={logo.alt}
        height={isDesktop ? 48 : undefined}
        src={logo.src}
        width={isDesktop ? undefined : logo.mobileWidth}
      />
    </Stack>
  );
};
CardLogo.propTypes = {
  logo: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    desktopWidth: PropTypes.string.isRequired,
    mobileWidth: PropTypes.string.isRequired
  }).isRequired
};

const CardTitle = ({ title, description }) => {
  const {
    sizes: { isMobile }
  } = useBreakpoints();
  return (
    <Stack
      direction="column"
      spacing={isMobile ? 0 : 1}
    >
      <Text
        size={isMobile ? 'lg' : 'md'}
        weight="medium"
      >
        {title}
      </Text>
      <Text size="sm">{description}</Text>
    </Stack>
  );
};
CardTitle.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

const CardFeatures = ({ features }) => (
  <>
    <Text
      size="sm"
      weight="medium"
    >
      What you get:
    </Text>
    <Stack spacing={1}>
      {features.map((feature) => (
        <Stack
          alignItems="center"
          direction="row"
          key={feature}
          spacing={1}
        >
          <SharedAsset
            alt="checkrounded icon"
            color="success.main"
            flex="none"
            src="/s2/material-icons/checkrounded.svg"
            width={16}
          />
          <Text size="sm">{feature}</Text>
        </Stack>
      ))}
    </Stack>
  </>
);
CardFeatures.propTypes = {
  features: PropTypes.arrayOf(PropTypes.node).isRequired
};

const CardDisclaimer = ({ disclaimer }) => (
  <Link
    href={disclaimer.hyperlink}
    rel="noreferrer"
    size="xs"
    target="_blank"
  >
    {disclaimer.anchorText}
  </Link>
);
CardDisclaimer.propTypes = {
  disclaimer: PropTypes.shape({
    hyperlink: PropTypes.string.isRequired,
    anchorText: PropTypes.string.isRequired
  }).isRequired
};

const CardDisclosure = ({ disclosure }) => (
  <Text
    color="tertiary"
    size="xs"
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'flex-end'
    }}
  >
    {disclosure}
  </Text>
);
CardDisclosure.propTypes = {
  disclosure: PropTypes.string.isRequired
};

const CardCTAButton = ({ ctaButton }) => {
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  return (
    <Button
      color="primary"
      fullWidth
      onClick={ctaButton.onClick}
      variant="contained"
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={1}
      >
        <Text size={isMobile ? 'xs' : 'md'}>{ctaButton.text}</Text>
        {ctaButton.icon && (
          <SharedAsset
            alt="button icon"
            color="common.white"
            src={ctaButton.icon}
            width={24}
          />
        )}
      </Stack>
    </Button>
  );
};
CardCTAButton.propTypes = {
  ctaButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.string
  }).isRequired
};

const OfferCard = ({
  id,
  logo,
  title,
  description,
  features,
  disclaimer,
  disclosure,
  ctaButton,
  isOpen,
  onToggle
}) => {
  const {
    sizes: { isTablet, isMobile }
  } = useBreakpoints();

  return (
    <CardContainer
      data-testid={id}
      minWidth={isTablet ? '45%' : 'auto'}
      padding={2}
    >
      <Stack>
        <CardLogo logo={logo} />
        <Box marginBlock={2}>
          <Divider />
        </Box>
      </Stack>
      <Stack
        height="100%"
        justifyContent="space-between"
      >
        <CardContent
          flex={1}
          spacing={2}
        >
          <CardTitle
            description={description}
            title={title}
          />
          {(!isMobile || isOpen) && <CardFeatures features={features} />}
          {disclaimer && <CardDisclaimer disclaimer={disclaimer} />}
          {disclosure && <CardDisclosure disclosure={disclosure} />}
        </CardContent>
        <CardAction>
          <CardCTAButton ctaButton={ctaButton} />
        </CardAction>
        {isMobile && (
          <CardAction>
            <Button
              color="info"
              endIcon={!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              fullWidth
              onClick={onToggle}
              size={isMobile ? 'sm' : 'md'}
              variant="text"
            >
              {!isOpen ? 'Show more' : 'Show less'}
            </Button>
          </CardAction>
        )}
      </Stack>
    </CardContainer>
  );
};
OfferCard.propTypes = {
  id: PropTypes.string.isRequired,
  logo: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    desktopWidth: PropTypes.string.isRequired,
    mobileWidth: PropTypes.string.isRequired
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.node).isRequired,
  disclaimer: PropTypes.shape({
    hyperlink: PropTypes.string.isRequired,
    anchorText: PropTypes.string.isRequired
  }),
  disclosure: PropTypes.string,
  ctaButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
};

export default OfferCard;
